import type {
  OrganizationFrame,
  OrganizationMemberIdentityResponse,
  RelationshipStatus,
} from "@kontextwerk/control-web-client"
import { getAdminApi, getControlWebClient } from "../client"
import {
  captureMembershipSettings,
  listCapturedMembershipSettings,
  type MembershipSettings,
} from "../membership-settings"
import {
  listFrameMembershipStatuses,
  type FrameMembershipAccess,
} from "./_logic/membership-statuses"

export type { FrameMembershipAccess } from "./_logic/membership-statuses"

/**
 * Roster member of the KB organization frame. Membership facts are only present when the
 * confirmed context may read memberships; `email` additionally requires membership management.
 */
export type KehrblattFrameMember = OrganizationMemberIdentityResponse &
  Partial<MembershipSettings["settings"]> & {
    membership_status?: RelationshipStatus | null
    role_id?: string | null
    membership_revision?: number | null
    email?: string | null
  }
export type KehrblattOrganizationFrame = Omit<OrganizationFrame, "users"> & {
  users: KehrblattFrameMember[]
}

export type OrganizationFrameLoadContext = {
  initialOrganization?: OrganizationFrame | null
}

type OrganizationUserSettingsItem = Pick<
  MembershipSettings,
  "organization_id" | "membership_id" | "user_id" | "settings"
>

/** Typed view of the roster members the KB frame loader attached to an organization frame. */
export const kehrblattFrameMembers = (
  organization: Pick<OrganizationFrame, "users"> | null | undefined,
): readonly KehrblattFrameMember[] => (organization?.users ?? []) as KehrblattFrameMember[]

/** Profile images may be served relative to the Control API origin. */
export const resolveFrameMemberAvatarUrl = (value: string | null | undefined): string | null => {
  const trimmed = (value ?? "").trim()

  if (!trimmed) return null

  if (/^(?:https?:|data:|blob:|\/\/)/i.test(trimmed)) return trimmed

  try {
    const base = getControlWebClient().settings.baseUrl.replace(/\/+$/, "")

    return `${base}${trimmed.startsWith("/") ? "" : "/"}${trimmed}`
  } catch {
    return trimmed
  }
}

export const mergeOrganizationFrameUserSettings = (
  organization: OrganizationFrame,
  settingsItems: OrganizationUserSettingsItem[],
  memberships: ReadonlyMap<string, FrameMembershipAccess> = new Map(),
): KehrblattOrganizationFrame => {
  const users = organization.users ?? []

  const members = new Map(users.map((user) => [user.membership_id, user]))
  const settingsByMembershipId = new Map<string, OrganizationUserSettingsItem["settings"]>()
  for (const item of settingsItems) {
    const member = members.get(item.membership_id)

    if (
      !member ||
      item.organization_id !== organization.id ||
      member.organization_id !== organization.id ||
      member.id !== item.user_id
    )
      continue

    if (settingsByMembershipId.has(item.membership_id))
      throw new TypeError("Duplicate membership settings")

    settingsByMembershipId.set(item.membership_id, item.settings)
  }

  return {
    ...organization,
    users: users.map((user): KehrblattFrameMember => {
      const settings = settingsByMembershipId.get(user.membership_id)
      const access = memberships.get(user.membership_id)

      return {
        ...user,
        membership_status: access?.status ?? null,
        ...(access
          ? { role_id: access.role_id, membership_revision: access.revision, email: access.email }
          : {}),
        ...(settings
          ? {
              availability_policy_id: settings.availability_policy_id,
              availability_policy: settings.availability_policy,
              daily_briefing: settings.daily_briefing,
            }
          : {}),
      }
    }),
  }
}

export const loadKehrblattOrganizationFrame = async (
  identifier: string,
  context: OrganizationFrameLoadContext,
): Promise<KehrblattOrganizationFrame> => {
  const initial = context.initialOrganization
  const frame =
    initial && (initial.id === identifier || initial.slug === identifier)
      ? initial
      : await getAdminApi().organization.registry.getFrame(identifier)
  const users = frame.users ?? []

  if (users.some((member) => member.organization_id !== frame.id))
    throw new TypeError("Foreign organization roster member")

  const normalizedFrame: OrganizationFrame = { ...frame, users }

  if (!users.length) return normalizedFrame

  const binding = captureMembershipSettings(frame.id)
  const items = await listCapturedMembershipSettings(binding, binding.signal)

  if (!binding.current())
    throw new DOMException("Obsolete organization frame settings", "AbortError")

  const memberships = await listFrameMembershipStatuses(binding, frame.id, users)

  if (!binding.current()) throw new DOMException("Obsolete organization frame status", "AbortError")

  return mergeOrganizationFrameUserSettings(normalizedFrame, items, memberships)
}
